import React, { useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Card from '../components/ui/Card';
import Button from '../components/ui/Button';
import Modal from '../components/ui/Modal';
import DateSelect from '../components/ui/DateSelect';
import SearchableChecklist from '../components/ui/SearchableChecklist';
import SearchableSelect from '../components/ui/SearchableSelect';
import { useStudentStore } from '../store/StudentStore';
import { useUser } from '../store/UserContext';
import StaffService from '../services/StaffService';
import { readFilesAsStoredAttachments } from '../utils/fileAttachments';

const incidentTypes = ['Injury', 'Safeguarding concern', 'Property damage', 'Theft', 'Behavioural', 'Other'];

export default function IncidentCreatePage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { students, incidentController, inventoryItems, inventoryController } = useStudentStore();
  const { user } = useUser();
  const staffService = useMemo(() => new StaffService(), []);
  const staffList = useMemo(() => staffService.list(), [staffService]);
  const staffOptions = useMemo(
    () =>
      staffList.map((staff) => ({
        value: staff.name,
        label: staff.name,
        meta: staff.role || staff.title || ''
      })),
    [staffList]
  );

  const preselectStudentId = searchParams.get('studentId') || '';

  const [incidentFiles, setIncidentFiles] = useState([]);
  const [incidentStudentSelection, setIncidentStudentSelection] = useState(
    preselectStudentId ? [preselectStudentId] : []
  );
  const [incidentStaffSelection, setIncidentStaffSelection] = useState([]);
  const [incidentInventorySelection, setIncidentInventorySelection] = useState([]);
  const [showIncidentInventoryModal, setShowIncidentInventoryModal] = useState(false);
  const [newIncidentInventoryItem, setNewIncidentInventoryItem] = useState({
    name: '',
    type: 'Tech Equipment',
    condition: 'Good'
  });
  const [incidentForm, setIncidentForm] = useState({
    type: 'Behavioural',
    description: '',
    date: new Date().toISOString().slice(0, 16),
    severity: 'medium',
    status: 'open',
    assignedKeyWorker: user?.name || '',
    followUpRequired: true,
    followUpPrompt: '',
    followUpDate: ''
  });
  const [incidentError, setIncidentError] = useState('');

  const toggleIncidentInventory = (itemId) => {
    setIncidentInventorySelection((prev) =>
      prev.includes(itemId) ? prev.filter((id) => id !== itemId) : [...prev, itemId]
    );
  };

  const toggleIncidentStudent = (studentId) => {
    setIncidentStudentSelection((prev) =>
      prev.includes(studentId) ? prev.filter((id) => id !== studentId) : [...prev, studentId]
    );
  };

  const toggleIncidentStaff = (staffId) => {
    setIncidentStaffSelection((prev) =>
      prev.includes(staffId) ? prev.filter((id) => id !== staffId) : [...prev, staffId]
    );
  };

  const handleAddIncidentInventoryItem = () => {
    if (!newIncidentInventoryItem.name.trim()) return;
    const item = inventoryController.createItem(
      {
        name: newIncidentInventoryItem.name.trim(),
        type: newIncidentInventoryItem.type || 'Other',
        condition: newIncidentInventoryItem.condition || 'Good',
        status: 'available'
      },
      user
    );
    setIncidentInventorySelection((prev) => [item.id, ...prev]);
    setNewIncidentInventoryItem({ name: '', type: 'Tech Equipment', condition: 'Good' });
    setShowIncidentInventoryModal(false);
  };

  const handleIncidentSubmit = async () => {
    if (incidentStudentSelection.length === 0) {
      setIncidentError('Select at least one student.');
      return;
    }
    if (!incidentForm.description.trim()) {
      setIncidentError('Description is required.');
      return;
    }
    let documents = [];
    try {
      const primaryStudent = students.find((s) => s.id === incidentStudentSelection[0]);
      documents = await readFilesAsStoredAttachments(incidentFiles, user?.name || 'System', {
        entityType: primaryStudent ? 'student' : 'incident',
        entityId: primaryStudent?.id || 'multi-student',
        entityName: primaryStudent?.name || 'Multi Student Incident',
        category: 'incidents',
        documentType: incidentForm.type || 'incident'
      });
    } catch (error) {
      setIncidentError(error.message || 'Unable to upload incident attachments.');
      return;
    }
    const participants = [
      ...incidentStudentSelection.map((studentId) => {
        const student = students.find((s) => s.id === studentId);
        return { id: studentId, type: 'student', name: student?.name || '', role: 'Student' };
      }),
      ...incidentStaffSelection.map((staffId) => {
        const staff = staffList.find((member) => member.id === staffId);
        return { id: staffId, type: 'staff', name: staff?.name || '', role: staff?.role || 'Support Worker' };
      })
    ];

    const created = incidentController.createIncident(
      {
        type: incidentForm.type,
        date: incidentForm.date,
        severity: incidentForm.severity,
        status: incidentForm.status,
        reportedBy: user?.name || 'System',
        description: incidentForm.description.trim(),
        assignedKeyWorker: incidentForm.assignedKeyWorker || '',
        followUpRequired: incidentForm.followUpRequired,
        followUpDate: incidentForm.followUpRequired ? incidentForm.followUpDate : null,
        followUpPrompt: incidentForm.followUpRequired ? incidentForm.followUpPrompt : '',
        participants,
        inventoryItemIds: incidentInventorySelection,
        documents
      },
      user
    );

    navigate(`/incidents/${created.id}`);
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h2 className="sp-value" style={{ fontSize: '20px', fontWeight: 600 }}>Log Incident</h2>
          <p className="sp-muted">Create a new incident report and link it to students and staff.</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="secondary" size="sm" onClick={() => navigate('/incidents')}>
            Cancel
          </Button>
          <Button size="sm" onClick={handleIncidentSubmit}>
            Save Incident
          </Button>
        </div>
      </div>

      {incidentError && (
        <div className="sp-alert sp-alert--critical text-sm">
          {incidentError}
        </div>
      )}

      <Card className="p-5 space-y-4">
        <div>
          <h3 className="sp-value" style={{ fontSize: '16px', fontWeight: 600 }}>Incident Details</h3>
          <p className="text-sm sp-muted">Core incident information and severity.</p>
        </div>
        <div className="grid md:grid-cols-3 gap-4">
          <div>
            <label className="sp-label">Logged by</label>
            <input
              className="sp-input mt-2 w-full"
              value={user?.name || 'System'}
              readOnly
              aria-label="Logged by"
            />
          </div>
          <div>
            <label className="sp-label">Type</label>
            <select
              value={incidentForm.type}
              onChange={(e) => setIncidentForm({ ...incidentForm, type: e.target.value })}
              className="sp-select mt-2 w-full"
            >
              {incidentTypes.map((type) => (
                <option key={type} value={type}>{type}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="sp-label">Date & time</label>
            <input
              type="datetime-local"
              value={incidentForm.date}
              onChange={(e) => setIncidentForm({ ...incidentForm, date: e.target.value })}
              className="sp-input mt-2 w-full"
            />
          </div>
          <div>
            <label className="sp-label">Severity</label>
            <select
              value={incidentForm.severity}
              onChange={(e) => setIncidentForm({ ...incidentForm, severity: e.target.value })}
              className="sp-select mt-2 w-full"
            >
              <option value="low">Low</option>
              <option value="medium">Medium</option>
              <option value="high">High</option>
              <option value="critical">Critical</option>
            </select>
          </div>
          <div>
            <label className="sp-label">Status</label>
            <select
              value={incidentForm.status}
              onChange={(e) => setIncidentForm({ ...incidentForm, status: e.target.value })}
              className="sp-select mt-2 w-full"
            >
              <option value="open">Open</option>
              <option value="monitoring">Monitoring</option>
              <option value="closed">Closed</option>
            </select>
          </div>
          <div>
            <label className="sp-label">Assigned key worker</label>
            <SearchableSelect
              value={incidentForm.assignedKeyWorker}
              options={[{ value: '', label: 'Unassigned' }, ...staffOptions]}
              placeholder="Search staff"
              emptyLabel="No staff found."
              ariaLabel="Search key worker"
              className="mt-2"
              onChange={(value) => setIncidentForm({ ...incidentForm, assignedKeyWorker: value })}
            />
            <div className="text-xs sp-muted mt-2">Assign the lead worker; add co-workers below.</div>
          </div>
          <div>
            <label className="sp-label">Follow-up required</label>
            <label className="flex items-center gap-2 text-sm mt-2">
              <input
                type="checkbox"
                checked={incidentForm.followUpRequired}
                onChange={(e) => setIncidentForm({ ...incidentForm, followUpRequired: e.target.checked })}
              />
              Enable follow-up tracking
            </label>
          </div>
          <div>
            <label className="sp-label">Follow-up due</label>
            <div className="mt-2">
              <DateSelect
                value={incidentForm.followUpDate}
                onChange={(value) =>
                  setIncidentForm((prev) => ({
                    ...prev,
                    followUpDate: value,
                    followUpRequired: value ? true : prev.followUpRequired
                  }))
                }
              />
            </div>
          </div>
          <div className="md:col-span-3">
            <label className="sp-label">Description</label>
            <textarea
              value={incidentForm.description}
              onChange={(e) => setIncidentForm({ ...incidentForm, description: e.target.value })}
              rows={4}
              className="sp-textarea mt-2 w-full"
            />
          </div>
          <div className="md:col-span-3">
            <label className="sp-label">Follow-up prompt</label>
            <textarea
              value={incidentForm.followUpPrompt}
              onChange={(e) => setIncidentForm({ ...incidentForm, followUpPrompt: e.target.value })}
              rows={3}
              className="sp-textarea mt-2 w-full"
              placeholder="Next steps / follow-up instructions"
            />
          </div>
        </div>
      </Card>

      <Card className="p-5 space-y-4">
        <div>
          <h3 className="sp-value" style={{ fontSize: '16px', fontWeight: 600 }}>People Involved</h3>
          <p className="text-sm sp-muted">Select students and staff linked to this incident.</p>
        </div>
        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <label className="sp-label">Students involved</label>
            <SearchableChecklist
              className="mt-2"
              items={students}
              selectedIds={incidentStudentSelection}
              onToggle={toggleIncidentStudent}
              placeholder="Search students"
              getLabel={(student) => student.name}
              getMeta={(student) => student.reference || student.id}
              emptyLabel="No students found."
            />
          </div>
          <div>
            <label className="sp-label">Co-workers / staff involved</label>
            <SearchableChecklist
              className="mt-2"
              items={staffList}
              selectedIds={incidentStaffSelection}
              onToggle={toggleIncidentStaff}
              placeholder="Search staff"
              getLabel={(staff) => staff.name}
              getMeta={(staff) => staff.role || staff.title || ''}
              emptyLabel="No staff found."
            />
          </div>
        </div>
      </Card>

      <Card className="p-5 space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="sp-value" style={{ fontSize: '16px', fontWeight: 600 }}>Related Inventory</h3>
            <p className="text-sm sp-muted">Link items to damage, loss, or theft.</p>
          </div>
          <Button size="sm" variant="ghost" type="button" onClick={() => setShowIncidentInventoryModal(true)}>
            Add item
          </Button>
        </div>
        <div className="grid md:grid-cols-2 gap-2">
          {inventoryItems.length > 0 ? (
            <SearchableChecklist
              items={inventoryItems}
              selectedIds={incidentInventorySelection}
              onToggle={toggleIncidentInventory}
              placeholder="Search inventory"
              getLabel={(item) => item.name}
              getMeta={(item) => item.reference || item.type}
              emptyLabel="No inventory items match."
              className="mt-2 md:col-span-2"
            />
          ) : (
            <p className="text-sm sp-muted">No inventory items available.</p>
          )}
        </div>
      </Card>

      <Card className="p-5 space-y-3">
        <div>
          <h3 className="sp-value" style={{ fontSize: '16px', fontWeight: 600 }}>Attachments</h3>
          <p className="text-sm sp-muted">Upload evidence, photos, or documents.</p>
        </div>
        <input
          type="file"
          multiple
          className="sp-input w-full"
          onChange={(e) => setIncidentFiles(Array.from(e.target.files || []))}
        />
        {incidentFiles.length > 0 && (
          <div className="text-xs sp-muted">
            {incidentFiles.map((file) => file.name).join(', ')}
          </div>
        )}
      </Card>

      <Modal isOpen={showIncidentInventoryModal} onClose={() => setShowIncidentInventoryModal(false)}>
        <div className="p-6 space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="sp-value" style={{ fontSize: '16px', fontWeight: 600 }}>Add Inventory Item</h3>
            <Button variant="ghost" size="sm" onClick={() => setShowIncidentInventoryModal(false)} type="button">
              Close
            </Button>
          </div>
          <div className="grid md:grid-cols-2 gap-4">
            <div className="md:col-span-2">
              <label className="sp-label">Item name</label>
              <input
                value={newIncidentInventoryItem.name}
                onChange={(e) => setNewIncidentInventoryItem({ ...newIncidentInventoryItem, name: e.target.value })}
                className="sp-input mt-2 w-full"
                placeholder="Item name"
              />
            </div>
            <div>
              <label className="sp-label">Type</label>
              <input
                value={newIncidentInventoryItem.type}
                onChange={(e) => setNewIncidentInventoryItem({ ...newIncidentInventoryItem, type: e.target.value })}
                className="sp-input mt-2 w-full"
                placeholder="Type"
              />
            </div>
            <div>
              <label className="sp-label">Condition</label>
              <input
                value={newIncidentInventoryItem.condition}
                onChange={(e) => setNewIncidentInventoryItem({ ...newIncidentInventoryItem, condition: e.target.value })}
                className="sp-input mt-2 w-full"
                placeholder="Condition"
              />
            </div>
          </div>
          <div className="flex items-center justify-end gap-2">
            <Button variant="secondary" size="sm" onClick={() => setShowIncidentInventoryModal(false)} type="button">
              Cancel
            </Button>
            <Button size="sm" onClick={handleAddIncidentInventoryItem}>
              Add Item
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
